// =============================================================================
// FILE: packages/renderer/src/lib/clientPortal.ts
// PURPOSE: API client for the invite-only Managed Client Portal. Uses a JWT
//   issued by the backend (/api/managed/client/*), scoped to the client's email
//   for full tenant isolation. Token persists in localStorage so clients can
//   freely leave and re-enter their VIP workspace.
// =============================================================================

const TOKEN_KEY = 'r2.client_token';
const EMAIL_KEY = 'r2.client_email';

export const clientToken = {
  get(): string | null { return localStorage.getItem(TOKEN_KEY); },
  email(): string | null { return localStorage.getItem(EMAIL_KEY); },
  set(token: string, email: string): void {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, email);
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
  },
};

async function jsonOrThrow(res: Response): Promise<Record<string, unknown>> {
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new Error((data.detail as string) || `Request failed (${res.status})`);
  return data;
}

export interface Phase { pct: number; billed: boolean; }
export interface NextPhase { index: number; pct: number; prevPct: number; amount: number; }
export interface ClientEngagement {
  id: string; clientName: string; clientEmail: string; projectName: string;
  budget: number; monthlyFee: number; royaltyPct: number; paymentMethod: string;
  payoutPaypal: string; phases: Phase[]; advance: number; spend: number;
  markups: number; prints: number; status: string;
  nextPhase: NextPhase | null; paidPct: number;
}
export interface ClientLedgerEntry { kind: string; amount?: number; at: string; method?: string; phasePct?: number; }
export interface ClientWorkspace {
  email: string;
  engagements: ClientEngagement[];
  ledger: ClientLedgerEntry[];
  budgetCap: number;
  payoutPaypal: string;
}

export async function clientLogin(email: string, password: string): Promise<{ token: string; email: string; clientName: string }> {
  const d = await jsonOrThrow(await fetch('/api/managed/client/login', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  }));
  return { token: d.token as string, email: d.email as string, clientName: (d.clientName as string) || '' };
}

export async function clientRegister(email: string, password: string, clientName: string): Promise<{ token: string; email: string; clientName: string }> {
  const d = await jsonOrThrow(await fetch('/api/managed/client/register', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, clientName }),
  }));
  return { token: d.token as string, email: d.email as string, clientName: (d.clientName as string) || clientName };
}

export async function clientWorkspace(): Promise<ClientWorkspace> {
  const token = clientToken.get();
  const d = await jsonOrThrow(await fetch('/api/managed/client/me', {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  }));
  return d as unknown as ClientWorkspace;
}

export async function payNextPhase(engagementId: string): Promise<{ mode: string; url?: string; amount: number; phasePct: number; message?: string; payoutPaypal?: string }> {
  const d = await jsonOrThrow(await fetch('/api/managed/phase/checkout', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ engagementId, origin: window.location.origin + window.location.pathname }),
  }));
  return d as unknown as { mode: string; url?: string; amount: number; phasePct: number; message?: string; payoutPaypal?: string };
}

export async function payAdvance(engagementId: string): Promise<{ mode: string; url?: string; amount: number; message?: string; payoutPaypal?: string }> {
  const d = await jsonOrThrow(await fetch('/api/managed/checkout', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ engagementId, origin: window.location.origin + window.location.pathname }),
  }));
  return d as unknown as { mode: string; url?: string; amount: number; message?: string; payoutPaypal?: string };
}

export async function checkoutStatus(sessionId: string): Promise<{ payment_status: string; status: string; amount: number }> {
  const d = await jsonOrThrow(await fetch(`/api/managed/checkout/status/${sessionId}`));
  return d as unknown as { payment_status: string; status: string; amount: number };
}

export interface ClientInvoice {
  id: string; invoiceNo: string; engagementId: string; projectName: string;
  phasePct: number; phaseAmount: number; monthlyFee: number; total: number;
  payUrl: string; status: string; dueDate: string; fingerprint: string; created_at: string;
}

export async function clientInvoices(): Promise<ClientInvoice[]> {
  const token = clientToken.get();
  const d = await jsonOrThrow(await fetch('/api/managed/client/invoices', {
    headers: { Authorization: `Bearer ${token ?? ''}` },
  }));
  return (d.invoices as ClientInvoice[]) ?? [];
}

// Fetch a server-rendered PDF (auth header) and open it in a new tab.
export async function openServerPdf(url: string, headers: HeadersInit): Promise<void> {
  const res = await fetch(url, { headers });
  if (!res.ok) throw new Error(`Could not load document (${res.status})`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  window.open(objUrl, '_blank');
  window.setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
}

export async function openClientPdf(url: string): Promise<void> {
  await openServerPdf(url, { Authorization: `Bearer ${clientToken.get() ?? ''}` });
}

// ── Founder-side invite management (master admin header) ────────────────────
function adminHeaders(email: string): HeadersInit {
  return { 'Content-Type': 'application/json', 'x-admin-email': email };
}

export interface ManagedInvite { id: string; email: string; clientName: string; status: string; created_at: string; }
export interface ManagedClientRow { email: string; clientName: string; created_at: string; }
export interface AdminEngagement {
  id: string; clientName: string; clientEmail: string; projectName: string;
  budget: number; monthlyFee: number; status: string; paidPct?: number;
  phases: Phase[]; nextPhase: NextPhase | null;
}

export async function listAllEngagements(adminEmail: string): Promise<AdminEngagement[]> {
  const d = await jsonOrThrow(await fetch('/api/admin/managed/engagements', { headers: { 'x-admin-email': adminEmail } }));
  return (d.engagements as AdminEngagement[]) ?? [];
}

export async function advancePhase(adminEmail: string, engagementId: string): Promise<{ invoiceNo: string; phasePct: number; total: number; emailed: boolean; payUrl: string; pdfUrl: string }> {
  const d = await jsonOrThrow(await fetch(`/api/admin/managed/engagements/${engagementId}/advance`, {
    method: 'POST', headers: adminHeaders(adminEmail),
    body: JSON.stringify({ origin: window.location.origin + window.location.pathname }),
  }));
  return d as unknown as { invoiceNo: string; phasePct: number; total: number; emailed: boolean; payUrl: string; pdfUrl: string };
}

export async function openAdminPdf(url: string, adminEmail: string): Promise<void> {
  await openServerPdf(url, { 'x-admin-email': adminEmail });
}

export async function createInvite(adminEmail: string, email: string, clientName: string): Promise<ManagedInvite> {
  const d = await jsonOrThrow(await fetch('/api/admin/managed/invites', {
    method: 'POST', headers: adminHeaders(adminEmail),
    body: JSON.stringify({ email, clientName }),
  }));
  return d as unknown as ManagedInvite;
}

export async function listInvites(adminEmail: string): Promise<{ invites: ManagedInvite[]; clients: ManagedClientRow[] }> {
  const d = await jsonOrThrow(await fetch('/api/admin/managed/invites', { headers: { 'x-admin-email': adminEmail } }));
  return d as unknown as { invites: ManagedInvite[]; clients: ManagedClientRow[] };
}

export async function revokeInvite(adminEmail: string, inviteId: string): Promise<void> {
  await jsonOrThrow(await fetch(`/api/admin/managed/invites/${inviteId}`, {
    method: 'DELETE', headers: { 'x-admin-email': adminEmail },
  }));
}
